// File: frontend/src/components/admin/common/AdminFieldInfoTooltip.tsx

import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { FaInfoCircle } from "react-icons/fa";
import "./AdminFieldInfoTooltip.css";

interface AdminFieldInfoTooltipProps {
  /** Short heading shown at the top of the tooltip panel. */
  title: string;
  /** Bullet lines listed under the heading — one line per place the field is used. */
  items: string[];
  /** Optional extra class for one-off placement tweaks. */
  className?: string;
}

/**
 * Small (i) icon that reveals a floating panel listing every place a field's
 * value is shown across the site — left pane, match title, URL slug, entry
 * references, etc. — so an admin can predict the effect of a change before
 * saving it.
 *
 * Rendered through a portal (same pattern as the row tooltip in MatchRow.tsx)
 * so the panel is never clipped by a modal's own `overflow-y: auto` /
 * `max-height` rules. Opens on hover for desktop and on click/tap for touch
 * devices, which have no hover state; closes on outside click, Escape, or on
 * leaving both the icon and the panel.
 */
export const AdminFieldInfoTooltip: React.FC<AdminFieldInfoTooltipProps> = ({
  title,
  items,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  const updatePosition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ x: rect.left + rect.width / 2, y: rect.bottom + 6 });
  };

  const handleOpen = () => {
    updatePosition();
    setIsOpen(true);
  };

  const handleClose = () => setIsOpen(false);

  // Close on outside click / Escape — needed because the panel also opens on tap,
  // which has no natural "mouse left the icon" moment to close it again.
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <span className={`admin-field-info${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="admin-field-info__trigger"
        onMouseEnter={handleOpen}
        onMouseLeave={handleClose}
        onClick={(e) => {
          // Toggle on click so touch devices (no hover) can open/close it too
          e.stopPropagation();
          e.preventDefault();
          if (isOpen) {
            handleClose();
          } else {
            handleOpen();
          }
        }}
        aria-label={title}
      >
        <FaInfoCircle />
      </button>
      {isOpen && ReactDOM.createPortal(
        <div
          className="admin-field-info__panel"
          style={{ left: position.x, top: position.y }}
          // Keep the panel open while the pointer moves from the icon onto it
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
        >
          <div className="admin-field-info__title">{title}</div>
          <ul className="admin-field-info__list">
            {items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </span>
  );
};